/* eslint-disable no-param-reassign */
/* eslint-disable default-case */
/* eslint-disable @typescript-eslint/no-unsafe-call */
// @ts-nocheck
import { type Layer, type HXKLineLayerSpec, defineHXKLineLayer } from '../../../core/layer';
import type { StockData, ExtendTagSeries } from '../helper/hxKLine';
import type { KLineTooltipItem } from '../ui/kLineTooltip/KLineTooltip';
import { Option as StandardChartOption } from '../helper/standardChart';

HXKline.setHXKlineChart(HXKlineChart);

const visInfo: Layer['visInfo'] = {
  id: 'hxKLineAndTag',
  name_en: 'Candlestick Chart And Tag',
  name_zh: '行情图叠加标签',
  types_en: [],
  purpose_en: [],
  purpose_zh: [],
  standers: '',
  parameters: {},
  extra: {
    isNotTrainedByLLM: true
  }
};

export interface Spec extends HXKLineLayerSpec {
  /** 标签使用的数据集索引，默认为 0 */
  dataIndex?: number;
  /** 标签数据编码 */
  encoding: {
    from: string;
    to: string;
    concepts: string;
  };
  stocks: StockData[];
  tradeDate?: string[];
  customEvent?: (event: unknown) => void;
  showLegend?: boolean;
  showTooltip?: boolean;
  dataZoom?: StandardChartOption['dataZoom'];
}

/** 暂不支持legend的点击功能 */
export const hxKLineAndTag = defineHXKLineLayer<Spec>({
  type: visInfo.id,
  isIndependentLayer: true,
  visInfo,
  // eslint-disable-next-line max-lines-per-function, sonarjs/cognitive-complexity
  render(api, spec, $dom) {
    const { layout, data: dataArray, hook, util, ui, token } = api;
    let legendIns: InstanceType<(typeof ui)['Legend']['Legend']>;
    let kLineTooltipIns: InstanceType<(typeof ui)['KLineTooltip']>;
    let hxKLine: { destroy: () => void };
    let dataZoomIns: InstanceType<(typeof ui)['CustomDataZoom']>;

    HXKline.verifyProjectPermission(hook.globalConfig(api).hxKLineVerifyInfo);
    HXKline.setGlobalConfig({
      // 禁止加载更多数据
      isForbiddenLoadMore: true
    });

    // * -------------------------- dom
    const $toolbarLegend = document.createElement('section');
    const $tooltipContainer = document.createElement('section');
    const $chart = document.createElement('section');
    const $kline = document.createElement('section');
    const $dataZoomContainer = document.createElement('section');
    const lineColor = token?.dvHXKLine?.constants?.DEFAULT_TICK_LINE_COLOR;
    const borderLineStyle = `1px solid ${lineColor || 'rgba(125, 133, 146, 0.3)'}`;

    $dom.style.cssText +=
      'display: flex; flex-direction: column; justify-content: center; align-items: center;';

    $toolbarLegend.style.cssText = 'width: 100%; margin-bottom: 8px;';
    $chart.style.cssText =
      'position: relative; flex: 1; width: 100%; height: 0; display: flex; flex-direction: row;';
    $kline.style.cssText = `
      position: relative;
      flex: 1;
      width: 0;
      height; 100%;
      border-right: ${borderLineStyle};
      border-top: ${borderLineStyle};
    `;
    $kline.setAttribute('id', util.common.generateUID());

    $chart.appendChild($kline);
    $dom.appendChild($toolbarLegend);
    $dom.appendChild($tooltipContainer);
    $dom.appendChild($chart);
    $dom.appendChild($dataZoomContainer);

    // * -------------------------- data
    const {
      dataIndex = 0,
      encoding: { from, to, concepts },
      tradeDate,
      stocks,
      showLegend = false,
      customEvent,
      dataZoom = {},
      showTooltip = true
    } = spec;
    const presetOption = hook.buildStandardChartPresetOption(api);
    const data = dataArray[dataIndex];
    const colorMap = presetOption?.color ?? [];
    const defaultToken = {
      text: {
        color: 'white',
        fontSize: 12
      },
      rect: {
        borderColor: 'rgba(128, 128, 128, 0.7)',
        backgroundColor: 'rgba(128, 128, 128, 0.1)',
        hoverBackgroundColor: 'rgba(128, 128, 128, 0.3)',
        activeBackgroundColor: 'rgba(128, 128, 128, 0.5)',
        activeBorderColor: 'rgba(128, 128, 128, 0.5)',
        hoverBorderColor: 'rgba(128, 128, 128, 0.3)'
      }
    };
    // * -------------------------- token主题
    const tagToken = util.data.deepMerge(defaultToken, token?.dvHXKLine?.hxKLineAndTag ?? {}, {
      override: true
    });

    // * -------------------------- 标签覆盖物数据
    const riseCycleList = data.values.map((item, index) => {
      const begin = new Date(item[from]);
      begin.setHours(0, 0, 0, 0);
      const end = new Date(item[to]);
      end.setHours(0, 0, 0, 0);
      return {
        id: index,
        beginTimestamp: begin.getTime(),
        endTimestamp: end.getTime(),
        borderColor: tagToken.rect.borderColor,
        backgroundColor: tagToken.rect.backgroundColor,
        titleList: item[concepts],
        hoverBackgroundColor: tagToken.rect.hoverBackgroundColor,
        activeBackgroundColor: tagToken.rect.activeBackgroundColor,
        activeBorderColor: tagToken.rect.activeBorderColor,
        textSize: tagToken.text.fontSize,
        hoverBorderColor: tagToken.rect.hoverBorderColor,
        isActive: index === data.values.length - 1,
        isHover: false,
        onClick: event => {
          if (typeof customEvent === 'function') {
            customEvent(data.values[event.id]);
          }
        }
      };
    });

    // * -------------------------- legend
    const selectedStockIndex = 0;
    let selectedStock = stocks[selectedStockIndex];

    const legendData = data.values
      .map(value => {
        return value[concepts];
      })
      .flat()
      .map((item, index) => ({
        name: item,
        color: colorMap[index % colorMap.length],
        type: util.const.SERIES_2_LEGEND_TYPE_MAP.bar,
        selected: true,
        disableSelect: true
      }));
    legendData.push({
      type: ui.Legend.ShowType.KLine,
      stocks,
      defaultValueIndex: selectedStockIndex
    });

    if (showLegend) {
      legendIns = new ui.Legend.Legend($toolbarLegend, {
        data: legendData,
        layout: 'pagination',
        token
      });
    }

    // * -------------------------- dataZoom
    let xAxisData = tradeDate ?? [new Date(), new Date()];
    dataZoomIns = new ui.CustomDataZoom($dataZoomContainer, {
      data: xAxisData,
      token,
      style: dataZoom
    });

    /** 行情图的tooltip */
    if (showTooltip) {
      kLineTooltipIns = new ui.KLineTooltip($tooltipContainer, {
        token
      });
    }

    function showKLineTooltip(kLineIns: unknown, dataIndex_?: number | undefined) {
      const {
        defaultTooltipData: defaultKLineTooltip
      }: { defaultTooltipData: KLineTooltipItem[] } = hook.getHXKLineTooltipData(
        kLineIns,
        'kline',
        dataIndex_
      );

      const { LAYOUT } = util.const;
      let showTooltip = defaultKLineTooltip;
      if (layout === LAYOUT.SMALL || layout === LAYOUT.SMALL_1_2) {
        // 设计师规定，卡片宽度较小时只显示三项tooltip字段
        showTooltip = showTooltip.slice(0, 3);
      }
      kLineTooltipIns?.show(showTooltip);
    }

    // * -------------------------- chart
    let isFirstInit = true;
    let dataList;
    function renderHXKLine() {
      if (hxKLine) {
        HXKline.disposeChart(
          (hxKLine._chartContainer as HTMLElement).parentElement?.getAttribute('id'),
          (...args) => {
            console.log('hxKLine destroy', ...args);
          }
        );
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      hxKLine = HXKline.initChart(
        $kline.getAttribute('id'),
        /**
         * @see https://khtest.10jqka.com.cn/kodexplorer/data/User/wangyifan/home/HXKlineChart/docs/guide/datafeed-datasource.html
         */
        {
          id: util.common.generateUID(),
          code: selectedStock.code,
          market: selectedStock.market,
          data_class: 'kline',
          time_period: 'day_1',
          trade_date: isFirstInit && typeof tradeDate === 'undefined' ? 0 : tradeDate,
          adjust_type: 'forward',
          subscribe_type: 'noUpdate',
          // 禁用滚动
          isForbiddenLoadMore: true,
          // 禁用更新
          isForbiddenUpdate: true
        },
        /**
         * @see https://khtest.10jqka.com.cn/kodexplorer/data/User/wangyifan/home/HXKlineChart/docs/guide/chart-api.html
         */
        {
          chartType: '',
          dateFormat: 'YYYY-MM-DD',
          locale: 'zh_CN',
          styles: ['customTheme', tagToken?.option]
        },
        (...args) => {
          console.log(...args);

          if (args[0] === 'success') {
            hxKLine.setZoomEnabled(false);
            hxKLine.setScrollEnabled(false);
            hxKLine.setBarSpaceLimit({ min: 0, max: 50 });
            hxKLine.zoomToShowAllData();
            dataList = args[1].data;

            if (isFirstInit) {
              const chartDataList = args[1].data;
              xAxisData = chartDataList.map(item => {
                return util.data.formatDate(item.timestamp);
              });
              dataZoomIns.update(xAxisData);
              isFirstInit = false;
              customEvent(data.values[data.values.length - 1]);
            }

            hxKLine.createOverlay({
              name: 'tag',
              extendData: riseCycleList,
              points: [{}]
            });

            // 处理 dom 相关的问题
            const $xAxis = $kline.querySelector(
              'div > div:nth-child(2) > div:nth-child(1)'
            ) as HTMLElement;
            const $yAxis = $kline.querySelector('div > div > div:nth-child(2)') as HTMLElement;
            $xAxis.style.cursor = 'default';
            $yAxis.style.cursor = 'default';

            // 初始tooltip展示最近日期的数据
            showKLineTooltip(hxKLine);

            /**
             * - gap 限制主图内容的上下间隔
             * - scrollZoomEnabled 限制y轴的拖动
             */
            hxKLine.setPaneOptions({
              id: 'candle_pane',
              gap: {
                top: 0.05,
                bottom: 0.05
              },
              axisOptions: { scrollZoomEnabled: false }
            });

            /** 实现自定义 tooltip */
            hxKLine.subscribeAction('onCrosshairChange', (...rests) => {
              if (!rests[0].kLineData) {
                return;
              }

              showKLineTooltip(hxKLine, rests[0].dataIndex);
            });
          }
        }
      );
    }

    dataZoomIns.on('dataZoom', (startIndex, endIndex) => {
      const sliceData = dataList.slice(startIndex, endIndex + 1);
      hxKLine.applyNewData(sliceData, false, () => {
        hxKLine.zoomToShowAllData();
      });
    });

    dataZoomIns.on('valueRange', valueRange => {
      if (!dataList) {
        return;
      }
      const sliceData = dataList.slice(valueRange[0], valueRange[1] + 1);
      hxKLine.applyNewData(sliceData, false, () => {
        hxKLine.zoomToShowAllData();
      });
    });

    legendIns?.klineDropdown?.[0].on('change', event => {
      selectedStock = stocks.find(item => item.code === event.key);

      renderHXKLine();
    });

    renderHXKLine();

    return () => {
      legendIns?.destroy?.();
      kLineTooltipIns?.destroy?.();
      dataZoomIns?.destroy?.();
      HXKline.disposeChart($kline, (...args) => {
        console.log('hxKLine destroy', ...args);
      });

      $dom.innerHTML = '';
    };
  }
});
